import { useMutation, useQueries } from "@tanstack/react-query";
import { useContext } from "react";
import { z } from "zod";

import { ApiFetchFunction, LdoAuthContext } from "@nens/ldo/auth-v1";
import { useParsedQuery } from "@nens/react-query-utils";
import { combineUrlAndParams } from "@nens/shared-utils";

import { API_BASE_URL, AuthMode } from "../constants";
import { dateSchema } from "../dates";
import { FilterSet } from "../domain/filter-set";
import { Scenario } from "../domain/scenario";
import { LdoPaginated, paginatedSchema } from "../pagination";
import { MetaModelsContext } from "../providers/meta-models-provider";

import { MetaModel } from "./meta-model";
import { useInvalidateCache, useQueryKey } from "./query-keys";

const breachSchema = z.object({
  lon: z.number(),
  lat: z.number(),
});

const filesSchema = z.record(
  z.string(),
  z.object({
    type: z.string(),
    file: z
      .object({
        created_at: dateSchema,
        size: z.number(),
        etag: z.string(),
      })
      .nullable(),
  })
);

export type FilesSchema = z.infer<typeof filesSchema>;

export const scenarioStatusSchema = z.union([
  z.literal("incomplete"),
  z.literal("files_present"),
  z.literal("processing"),
  z.literal("complete"),
  z.literal("import_checked"),
  z.literal("quality_control_requested"),
  z.literal("quality_checked"),
  z.literal("quality_rejected"),
  z.literal("archived"),
]);

export type ScenarioStatus = z.infer<typeof scenarioStatusSchema>;
export const SCENARIO_STATUSES: ScenarioStatus[] = [
  "incomplete",
  "files_present",
  "processing",
  "complete",
  "import_checked",
  "quality_control_requested",
  "quality_checked",
  "quality_rejected",
  "archived",
];

/**
 * Checks if the given scenario status is considered "ready". Only "ready" scenarios can be exported.
 * A scenario is considered "ready" if its status is not one of the following:
 * - "incomplete"
 * - "files_present"
 * - "processing"
 * - "archived"
 * - "complete"
 * @param status The scenario status to check.
 * @returns True if the status is "ready", false otherwise.
 */
export function isReadyStatus(status: ScenarioStatus) {
  return !(
    status === "incomplete" ||
    status === "files_present" ||
    status === "processing" ||
    status === "archived" ||
    status === "complete"
  );
}

export const scenarioListItemSchema = z.object({
  id: z.number(),
  tenant: z.number(),
  breaches: z.array(breachSchema),
  created_at: dateSchema,
  created_by: z.object({
    id: z.string(),
    name: z.string(),
  }),
  files: filesSchema,
  updated_at: dateSchema,
  updated_by: z.object({
    id: z.string(),
    name: z.string(),
  }),
  name: z.string(),
  status: scenarioStatusSchema,
  meta: z.record(z.string(), z.string().or(z.number()).or(z.boolean()).nullable()),
  meta_version: z.number(),
  excel_import_id: z.number().nullable(),
  rasters: z.array(
    z.object({
      name: z.string(),
      type: z.string(),
      id: z.string(),
    })
  ),
  errors: z.array(z.object({ msg: z.string() })),
});

export const scenarioListSchema = paginatedSchema(scenarioListItemSchema);

// Note that the actual "Scenario" is a domain class in ../domain/scenario"
export type ApiScenario = z.infer<typeof scenarioListItemSchema>;
export type PaginatedScenarioList = LdoPaginated<Scenario>;

export function useScenarios(params = {}, filters?: FilterSet) {
  const { getMetaModelById } = useContext(MetaModelsContext);
  const url =
    combineUrlAndParams(`${API_BASE_URL}/scenarios`, params) +
    (filters ? "&" + filters.toUrlParams() : "");

  const { apiFetchFunction } = useContext(LdoAuthContext);

  const queryKey = useQueryKey(["scenarios", "list"]);

  return useParsedQuery<PaginatedScenarioList>(
    [...queryKey, url],
    () => apiFetchFunction(url),
    (json: unknown) => {
      const apiScenarios = scenarioListSchema.parse(json);
      // Turn API scenario responses into Scenario objects.
      return {
        ...apiScenarios,
        items: apiScenarios.items.map(
          (apiScenario) => new Scenario(apiScenario, getMetaModelById(apiScenario.id))
        ),
      };
    }
  );
}

export async function fetchScenariosInFilters(
  apiFetchFunction: ApiFetchFunction,
  getMetaModelById: (id: number) => MetaModel,
  authMode: AuthMode,
  filters: FilterSet
): Promise<Scenario[]> {
  const scenarios = [];

  let apiScenarios: LdoPaginated<ApiScenario>;
  do {
    const response = await apiFetchFunction(
      `${API_BASE_URL}/scenarios?mode=${authMode}&offset=${
        scenarios.length
      }&limit=100&${filters.toUrlParams()}`
    );
    apiScenarios = scenarioListSchema.parse(response);
    scenarios.push(
      ...apiScenarios.items.map(
        (apiScenario) => new Scenario(apiScenario, getMetaModelById(apiScenario.id))
      )
    );
  } while (scenarios.length < apiScenarios.total);

  return scenarios;
}

export function useScenario(id: number) {
  const { getMetaModelById } = useContext(MetaModelsContext);
  const url = `${API_BASE_URL}/scenarios/${id}`;
  const { apiFetchFunction } = useContext(LdoAuthContext);

  const queryKey = useQueryKey(["scenarios", "detail"]);

  return useParsedQuery<Scenario>(
    [...queryKey, id],
    () => apiFetchFunction(url),
    (json: unknown) => {
      const apiScenario = scenarioListItemSchema.parse(json);
      return new Scenario(apiScenario, getMetaModelById(apiScenario.id));
    }
  );
}

export function useMultipleScenarios(scenarioIds: number[]) {
  const { apiFetchFunction } = useContext(LdoAuthContext);
  const { getMetaModelById } = useContext(MetaModelsContext);

  const queryKey = useQueryKey(["scenarios", "detail"]);

  return useQueries({
    queries: scenarioIds.map((id) => ({
      queryKey: [...queryKey, id],
      queryFn: async () => {
        const response = await apiFetchFunction(`${API_BASE_URL}/scenarios/${id}`);
        const apiScenario = scenarioListItemSchema.parse(response);
        return new Scenario(apiScenario, getMetaModelById(apiScenario.id));
      },
    })),
  });
}

export const reviewSchema = z.object({
  content_quality: z.object({
    ok: z.boolean(),
    comment: z.string().nullable(),
  }),
  data_valid: z.object({
    ok: z.boolean(),
    comment: z.string().nullable(),
  }),
  metadata_complete: z.object({
    ok: z.boolean(),
    comment: z.string().nullable(),
  }),
  metadata_valid: z.object({
    ok: z.boolean(),
    comment: z.string().nullable(),
  }),
});

export const scenarioReviewSchema = z.object({
  id: z.number(), // Same as scenario
  created_at: dateSchema,
  created_by: z.object({
    id: z.string(),
    name: z.string(),
  }),
  updated_at: dateSchema,
  updated_by: z.object({
    id: z.string(),
    name: z.string(),
  }),
  name: z.string(), // Scenario name
  review: reviewSchema,
});

export type ScenarioReviewObject = z.infer<typeof scenarioReviewSchema>;
export type ScenarioReview = ScenarioReviewObject["review"];
export type SingleScenarioReview = ScenarioReview["content_quality"];

export const SCENARIO_REVIEW_FIELDS: (keyof ScenarioReview)[] = [
  "metadata_complete",
  "metadata_valid",
  "data_valid",
  "content_quality",
];

export function useScenarioReview(id: number) {
  // Note it is expected for this to error, that means there is no review yet.
  const url = `${API_BASE_URL}/scenarios/${id}/review`;
  const { apiFetchFunction } = useContext(LdoAuthContext);
  const queryKey = useQueryKey(["scenarios", "review"]);

  return useParsedQuery<ScenarioReviewObject>(
    // Note this key means it gets refreshed when the scenario is, that's
    // what we want
    [...queryKey, id],
    () => apiFetchFunction(url),
    (json: unknown) => scenarioReviewSchema.parse(json)
  );
}

export interface ScenarioUpdate {
  status: ScenarioStatus;
  review?: ScenarioReview;
}

export function useUpdateScenarioStatus(scenarioId: number) {
  const { apiFetchFunction } = useContext(LdoAuthContext);

  const refreshDetail = useInvalidateCache(["scenarios", "detail"], [scenarioId]);
  const refreshList = useInvalidateCache(["scenarios", "list"]);
  const refreshReview = useInvalidateCache(["scenarios", "review"], [scenarioId]);

  return useMutation({
    mutationFn: async (scenarioUpdate: ScenarioUpdate) => {
      const url = `${API_BASE_URL}/scenarios/${scenarioId}/status`;

      await apiFetchFunction(url, { method: "PATCH", body: JSON.stringify(scenarioUpdate) });

      await refreshDetail();
      await refreshReview();
      await refreshList();
    },
  });
}

export function useDeleteScenario(scenarioId: number) {
  const { apiFetchFunction } = useContext(LdoAuthContext);

  const refreshList = useInvalidateCache(["scenarios", "list"]);

  return useMutation({
    mutationFn: async () => {
      const url = `${API_BASE_URL}/scenarios/${scenarioId}`;

      try {
        await apiFetchFunction(url, { method: "DELETE" });
      } catch (e) {
        // This is expected; the response isn't JSON.
      }

      await refreshList();
    },
  });
}
